/**
 * Dutch license plate parsing, mirroring `AutoChecken.CarReports.LicensePlate` on the server.
 *
 * The server is the authority — it re-validates every plate and the report itself carries the
 * canonical `licensePlate` / `licensePlateCompact`. This exists so the search box can reject a
 * typo without a round trip, and so a URL can be normalised before it is navigated to.
 */

/** The longest Dutch plate is six characters. */
export const PLATE_LENGTH = 6

/**
 * The sidecodes as issued by the RDW, in order. The capture groups are the dash-separated groups,
 * which is what tells us where the dashes go — a single merged pattern could not.
 */
const SIDECODES: readonly RegExp[] = [
  /^([A-Z]{2})([0-9]{2})([0-9]{2})$/, //  1: XX-99-99
  /^([0-9]{2})([0-9]{2})([A-Z]{2})$/, //  2: 99-99-XX
  /^([0-9]{2})([A-Z]{2})([0-9]{2})$/, //  3: 99-XX-99
  /^([A-Z]{2})([0-9]{2})([A-Z]{2})$/, //  4: XX-99-XX
  /^([A-Z]{2})([A-Z]{2})([0-9]{2})$/, //  5: XX-XX-99
  /^([0-9]{2})([A-Z]{2})([A-Z]{2})$/, //  6: 99-XX-XX
  /^([0-9]{2})([A-Z]{3})([0-9])$/, //     7: 99-XXX-9
  /^([0-9])([A-Z]{3})([0-9]{2})$/, //     8: 9-XXX-99
  /^([A-Z]{2})([0-9]{3})([A-Z])$/, //     9: XX-999-X
  /^([A-Z])([0-9]{3})([A-Z]{2})$/, //    10: X-999-XX
  /^([A-Z]{3})([0-9]{2})([A-Z])$/, //    11: XXX-99-X
  /^([A-Z])([0-9]{2})([A-Z]{3})$/, //    12: X-99-XXX
  /^([0-9])([A-Z]{2})([0-9]{3})$/, //    13: 9-XX-999
  /^([0-9]{3})([A-Z]{2})([0-9])$/, //    14: 999-XX-9
]

/**
 * The same fourteen layouts written the way a person reads them, indexed by sidecode number.
 *
 * The API reports which sidecode a plate matched — it has to identify the layout anyway to know
 * where the dashes go — but sends only the number, so this is what turns "sidecode 9" into
 * something worth reading. Kept beside `SIDECODES` above so the two are edited together; a
 * fifteenth layout needs a line in both.
 */
const SIDECODE_PATTERNS: readonly string[] = [
  'XX-99-99',
  '99-99-XX',
  '99-XX-99',
  'XX-99-XX',
  'XX-XX-99',
  '99-XX-XX',
  '99-XXX-9',
  '9-XXX-99',
  'XX-999-X',
  'X-999-XX',
  'XXX-99-X',
  'X-99-XXX',
  '9-XX-999',
  '999-XX-9',
]

/** The layout of a sidecode, for example `XX-999-X` for sidecode 9. */
export function sidecodePattern(sidecode: number | undefined): string | undefined {
  return sidecode === undefined ? undefined : SIDECODE_PATTERNS[sidecode - 1]
}

/**
 * Every layout as a character mask plus the positions its dashes fall at, derived from the patterns
 * above so the two can never disagree.
 *
 * `XX-999-X` becomes mask `XX999X` with dashes after positions 2 and 5.
 */
const SIDECODE_SHAPES = SIDECODE_PATTERNS.map((pattern) => {
  const groups = pattern.split('-')

  return {
    mask: groups.join(''),
    dashes: groups.slice(0, -1).map((_, index) => groups.slice(0, index + 1).join('').length),
  }
})

function matchesMask(mask: string, compact: string): boolean {
  if (compact.length > mask.length) {
    return false
  }

  return [...compact].every((character, index) => {
    const expected = mask[index]
    return expected === 'X' ? /[A-Z]/.test(character) : /[0-9]/.test(character)
  })
}

/**
 * Groups a plate *while it is being typed*, so the dashes appear on their own.
 *
 * The catch is that a half-typed plate does not yet have a sidecode: `VP` is still on its way to
 * `XX-99-99`, `XX-99-XX`, `XX-XX-99` and `XX-999-X`, and those do not agree on where the first dash
 * goes. So this narrows the fourteen layouts to the ones the characters so far are still consistent
 * with, and inserts a dash only at a position **every** surviving layout agrees on. `VP` gets
 * nothing; `VPF7` rules out everything except `XXX-99-X` and becomes `VPF-7`.
 *
 * **A dash is only added when a character follows it.** Trailing dashes are the obvious touch and
 * they trap the caret: the field would render `12-`, backspace would delete the dash, the value
 * would normalise back to `12`, and this would put the dash straight back. The user cannot get out.
 *
 * Anything that matches no layout at all is returned ungrouped rather than rejected. Validation is
 * `parsePlate`'s job and happens on submit; a field that fights you mid-word is worse than one that
 * waits.
 */
export function formatPlateInput(input: string): string {
  const compact = normalizePlate(input).slice(0, PLATE_LENGTH)
  const candidates = SIDECODE_SHAPES.filter((shape) => matchesMask(shape.mask, compact))

  if (candidates.length === 0) {
    return compact
  }

  const first = candidates[0]
  if (!first) {
    return compact
  }

  const agreed = first.dashes.filter(
    (position) => position < compact.length && candidates.every((shape) => shape.dashes.includes(position)),
  )

  return [...compact].map((character, index) => (agreed.includes(index) ? `-${character}` : character)).join('')
}

/** How many plate layouts the RDW has issued, for the "one of fourteen" phrasing. */
export const SIDECODE_COUNT = SIDECODE_PATTERNS.length

export interface LicensePlate {
  /** Compact upper-case form without separators, for example `S038BL`. Used in URLs and requests. */
  compact: string
  /** Grouped with dashes as printed on the plate, for example `S-038-BL`. Used for display. */
  formatted: string
}

/**
 * Strips separators and upper-cases. The letters I, O and Q are never issued on Dutch plates, but
 * nothing is substituted for them: an ambiguous plate is a rejected plate, not a silently
 * corrected one.
 */
export function normalizePlate(input: string): string {
  return input.replace(/[-\s._]/g, '').toUpperCase()
}

/** Parses `input` into a plate, or returns `undefined` when it matches no sidecode. */
export function parsePlate(input: string | undefined | null): LicensePlate | undefined {
  if (!input) {
    return undefined
  }

  const compact = normalizePlate(input)
  if (compact.length !== PLATE_LENGTH) {
    return undefined
  }

  for (const sidecode of SIDECODES) {
    const match = sidecode.exec(compact)
    if (match) {
      return { compact, formatted: `${match[1]}-${match[2]}-${match[3]}` }
    }
  }

  return undefined
}

export function isValidPlate(input: string | undefined | null): boolean {
  return parsePlate(input) !== undefined
}

/**
 * Formats for display, falling back to the normalised input when it is not a valid plate. Used
 * where something has to be shown regardless — an error page naming the plate that failed, for
 * instance.
 */
export function formatPlate(input: string): string {
  return parsePlate(input)?.formatted ?? normalizePlate(input)
}
